<template>
    <Row class="CollateralMappingTableRow" hoverable>
        <Cell
            data-test-id="CollateralMappingTableRow__identifier"
        >
            <div class="flex flex-col">
                <span
                    data-test-id="CollateralMappingTableRow__description"
                    class="text-eidDarkBlue"
                >
                    {{ collateralEntity.description }}
                </span>
                <span data-test-id="CollateralMappingTableRow__uniqueIdentifier" class="text-eidDarkBlue-50">
                    {{ collateralEntity.uniqueIdentifier }}
                </span>
            </div>
        </Cell>
        <Cell class="w-0" data-test-id="CollateralMappingTableRow__providedOptions">
            <div
                class="flex justify-center items-center w-[400px] gap-2"
                data-test-id="CollateralMappingTableRow__providedCollateralContainer"
            >
                <div
                    v-if="!noMappingIcon"
                    class="flex justify-center item-center w-5"
                    data-test-id="CollateralMappingTableRow__iconContainer"
                >
                    <EidIcon
                        v-if="statusIcon"
                        :class="[
                            'CollateralMappingTableRow__statusIcon',
                            `CollateralMappingTableRow__statusIcon--${statusState}`,
                            { 'CollateralMappingTableRow__statusIcon--basedOnAction': optionsBasedOnAction }
                        ]"
                        :data-test-id="`CollateralMappingTableRow__statusIcon--${statusState}`"
                        :icon-src="statusIcon"
                    />
                </div>
                <Dropdown
                    class="flex-1"
                    data-test-id="CollateralMappingTableRow__providedOptionDropdown"
                    :placeholder="dropdownPlaceholder"
                    :options="options"
                    :selected="selectedOptions"
                    :disabled="isNoMatch || rbacDisabled"
                    @input="onCollateralSelection"
                />
            </div>
        </Cell>
        <Cell data-test-id="CollateralMappingTableRow__noMatch">
            <div
                class="flex justify-center items-center"
            >
                <Checkbox
                    :model-value="isNoMatch"
                    :disabled="rbacDisabled"
                    @update:model-value="toggleNoMatch"
                >
                    No Match
                </Checkbox>
                <div class="w-[20px]">
                    <EidIcon
                        v-if="shouldShowResetButton"
                        v-tooltip="'Clear selections made. You may save after clearing a selection.'"
                        class="w-[20px] text-eidDarkBlue-75 ml-2 cursor-pointer hover:opacity-70"
                        data-test-id="CollateralMappingTableRow__resetMappingIcon"
                        :icon-src="faRotateLeft"
                        :icon-props="{size: 'lg'}"
                        @click="resetMapping"
                    />
                </div>
            </div>
        </Cell>
        <Cell data-test-id="CollateralMappingTableRow__viewDocument">
            <DocumentDataOnlyBadge v-if="isShowingDataOnlyBadge" />
            <DocumentViewButton v-else-if="isShowingDocumentViewButton" @click="showDocument" />
        </Cell>
    </Row>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import isEqual from 'lodash/isEqual';

    import { faFileAlt, faUser } from '@fortawesome/free-regular-svg-icons';
    import { faExclamationTriangle, faRotateLeft } from '@fortawesome/free-solid-svg-icons';

    import { Checkbox } from '@evidentid/dashboard-commons/components/Checkbox';
    import { Dropdown } from '@evidentid/dashboard-commons/components/Dropdown';
    import { DropdownOption } from '@evidentid/dashboard-commons/components/Dropdown/types';
    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { Cell, Row } from '@evidentid/dashboard-commons/components/Table';
    import { SubmissionMethod } from '@evidentid/tprm-portal-lib/models/common/SubmissionMethod.model';
    import { ComplianceStatus } from '@evidentid/tprm-portal-lib/models/entity-details';
    import { CollateralEntity } from '@evidentid/tprm-portal-lib/models/entity-details/Collateral/CollateralEntity.model';
    import { CollateralMappingType } from '@evidentid/tprm-portal-lib/models/entity-details/Collateral/CollateralMappingType.model';
    import { ConciseCollateral } from '@evidentid/tprm-portal-lib/models/entity-details/Collateral/ConciseCollateral.model';

    import actionReviewSvg from '@/assets/icons/nav-icon-actions-review.svg';
    import DocumentDataOnlyBadge from '@/modules/entity-details/components/DocumentDataOnlyBadge/DocumentDataOnlyBadge.vue';
    import DocumentViewButton from '@/modules/entity-details/components/DocumentViewButton/DocumentViewButton.vue';
    import { CollateralMappingStatus } from '@/modules/entity-details/models/CollateralMappingStatus.model';
    import {
        getCollateralMappingOptionStatus,
    } from '@/modules/entity-details/utils/getCollateralMappingOptionStatus/getCollateralMappingOptionStatus';
    import {
        isFieldImportApiOrWithoutDocumentUpload,
    } from '@/modules/entity-details/utils/isFieldImportApiOrWithoutDocumentUpload/isFieldImportApiOrWithoutDocumentUpload';

    export default defineComponent({
        name: 'CollateralMappingTableRow',
        components: {
            DocumentViewButton,
            Cell,
            Checkbox,
            Dropdown,
            EidIcon,
            Row,
            DocumentDataOnlyBadge,
        },
        props: {
            collateralEntity: {
                type: Object as PropType<CollateralEntity>,
                default: (): CollateralEntity => ({}) as CollateralEntity,
            },
            providedCollaterals: {
                type: Array as PropType<ConciseCollateral[]>,
                default: (): ConciseCollateral[] => [] as ConciseCollateral[],
            },
            // the reason this prop needed is that we want to preserve icon space for any row that has no so
            // the dropdown will be aligned with the rows that has icon. however when all rows has no icon
            // we want to remove this preserved space so columns will align with header
            noMappingIcon: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            isNoMatch: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            optionsBasedOnAction: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            submissionMethod: {
                type: String as PropType<SubmissionMethod>,
                required: false,
                default: undefined,
            },
            rbacDisabled: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        emits: [ 'show-document', 'update-collateral', 'update-no-match' ],
        data() {
            return {
                faRotateLeft,
                selectedOptions: [] as DropdownOption[],
                viewDocumentIcon: faFileAlt,
                SubmissionMethod,
            };
        },
        computed: {
            statusState(): CollateralMappingStatus {
                if (this.isNoMatch) {
                    return CollateralMappingStatus.manual;
                }
                return getCollateralMappingOptionStatus(this.providedCollaterals, this.collateralEntity);
            },
            statusIcon(): any {
                if (this.optionsBasedOnAction && this.statusState === CollateralMappingStatus.auto) {
                    return actionReviewSvg;
                }

                switch (this.statusState) {
                    case CollateralMappingStatus.actionReview:
                        return actionReviewSvg;
                    case CollateralMappingStatus.manual:
                        return faUser;
                    case CollateralMappingStatus.error:
                        return faExclamationTriangle;
                    default:
                        return null;
                }
            },
            dropdownPlaceholder(): string {
                return this.isNoMatch ? 'N/A' : 'Choose Mapping';
            },
            isNonCompliantWithoutRequirement(): boolean {
                return this.collateralEntity?.complianceStatus === ComplianceStatus.nonCompliant
                    && !this.collateralEntity.coverageType;
            },
            hasValidCollateralForDocumentButtonOrBadge(): boolean {
                return Boolean(this.providedCollaterals?.length > 0 &&
                    !this.isNonCompliantWithoutRequirement &&
                    ![ ComplianceStatus.pending, ComplianceStatus.new ]
                        .includes(this.collateralEntity.complianceStatus),
                );
            },
            isFieldImportApiOrWithoutDocumentUploadVal(): boolean {
                return isFieldImportApiOrWithoutDocumentUpload({
                    submissionMethod: this.submissionMethod,
                });
            },
            isShowingDataOnlyBadge(): boolean {
                return this.hasValidCollateralForDocumentButtonOrBadge
                    && this.isFieldImportApiOrWithoutDocumentUploadVal;
            },
            isShowingDocumentViewButton(): boolean {
                return this.hasValidCollateralForDocumentButtonOrBadge
                    && !this.isFieldImportApiOrWithoutDocumentUploadVal;
            },
            shouldShowDocument(): boolean {
                return Boolean(this.providedCollaterals?.length > 0 &&
                    !this.isNonCompliantWithoutRequirement &&
                    ![ ComplianceStatus.pending, ComplianceStatus.new ]
                        .includes(this.collateralEntity.complianceStatus),
                );
            },
            options(): DropdownOption[] {
                return this.providedCollaterals.map((collateral) => {
                    const label = this.getCollateralLabel(collateral);
                    return {
                        value: { ...collateral },
                        label,
                    };
                });
            },
            selectedCollateral(): ConciseCollateral | null {
                return this.selectedOptions[0]?.value || null;
            },
            shouldShowResetButton(): boolean {
                return this.statusState === CollateralMappingStatus.manual && !this.rbacDisabled;
            },
        },
        watch: {
            collateralEntity: {
                immediate: true,
                handler() {
                    const option =
                        this.options.find((x) => isEqual(x.value, this.collateralEntity.mapping?.collateral));
                    this.selectedOptions = option ? [ option ] : [];
                },
            },
        },
        methods: {
            resetMapping(): void {
                this.selectedOptions = [];
                this.$emit('update-collateral', {
                    ...this.collateralEntity,
                    mapping: null,
                });
                if (this.isNoMatch) {
                    this.$emit('update-no-match', this.collateralEntity?.evaluatedEntityId);
                }
            },
            onCollateralSelection(option?: DropdownOption): void {
                if (option) {
                    this.selectedOptions = [ option ];
                }
                this.$emit('update-collateral', {
                    ...this.collateralEntity,
                    mapping: { mappingType: CollateralMappingType.manual, collateral: this.selectedCollateral },
                });
            },
            toggleNoMatch(checked: boolean): void {
                const mapping = !checked && !this.selectedCollateral
                    ? null
                    : { mappingType: CollateralMappingType.manual, collateral: this.selectedCollateral };

                this.$emit('update-collateral', {
                    ...this.collateralEntity,
                    mapping,
                });
                this.$emit('update-no-match', this.collateralEntity?.evaluatedEntityId);
            },
            showDocument(): void {
                this.$emit('show-document');
            },
            getCollateralLabel(collateral: ConciseCollateral): string {
                const notProvided = 'No description/identifier provided';
                const separator = collateral.description && collateral.uniqueIdentifier ? ' - ' : '';
                return !collateral.description && !collateral.uniqueIdentifier
                    ? notProvided
                    : `${collateral.description || ''}${separator}${collateral.uniqueIdentifier || ''}`;
            },
        },
    });
</script>
